import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  IconButton,
  Alert,
  InputAdornment,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  Close as CloseIcon,
  Folder as ProjectsIcon,
  Group as MembersIcon,
  Storage as StorageIcon,
  AttachMoney as PriceIcon
} from '@mui/icons-material';
import { colors } from '../../utils/colors';

const EditSubscriptionDialog = ({ open, onClose, subscription, onSave }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [formData, setFormData] = useState({
    number_of_projects: '',
    number_of_members_per_project: '',
    storage: '',
    price_per_month: ''
  });
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (subscription) {
      setFormData({
        number_of_projects: subscription.number_of_projects || '',
        number_of_members_per_project: subscription.number_of_members_per_project || '',
        storage: subscription.storage || '',
        price_per_month: subscription.price_per_month ?? ''
      });
      setErrors({});
      setError('');
    }
  }, [subscription]);

  const validateField = (name, value) => {
    const numValue = parseFloat(value);
    if (value === '' || value === null || value === undefined) {
      return 'This field is required';
    }
    if (isNaN(numValue) || numValue < 0) {
      return 'Must be a positive number';
    }
    if (name === 'number_of_projects' && numValue < 1) {
      return 'Must be at least 1 project';
    }
    if (name === 'number_of_members_per_project' && numValue < 1) {
      return 'Must be at least 1 member';
    }
    if (name === 'storage' && numValue < 1) {
      return 'Must be at least 1 MB';
    }
    return '';
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const handleBlur = (field) => {
    const error = validateField(field, formData[field]);
    if (error) {
      setErrors(prev => ({
        ...prev,
        [field]: error
      }));
    }
  };

  const handleSave = async () => {
    const newErrors = {};
    Object.keys(formData).forEach(field => {
      const error = validateField(field, formData[field]);
      if (error) {
        newErrors[field] = error;
      }
    });

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setSaving(true);
    setError('');

    try {
      const dataToSend = {
        number_of_projects: parseInt(formData.number_of_projects),
        number_of_members_per_project: parseInt(formData.number_of_members_per_project),
        storage: parseInt(formData.storage),
        price_per_month: parseFloat(formData.price_per_month)
      };

      await onSave(subscription.id, dataToSend);
      onClose();
    } catch (err) {
      setError(err.message || 'Failed to update subscription');
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    if (!saving) {
      setFormData({
        number_of_projects: '',
        number_of_members_per_project: '',
        storage: '',
        price_per_month: ''
      });
      setErrors({});
      setError('');
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          borderRadius: isMobile ? 0 : 2,
          maxHeight: isMobile ? '100%' : '90vh'
        }
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              fontSize: { xs: '1.1rem', sm: '1.25rem' }
            }}
          >
            Edit {subscription?.name} Plan
          </Typography>
          <IconButton
            onClick={handleClose}
            size="small"
            disabled={saving}
            sx={{ color: colors.text.secondary }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <Box>
            <Typography
              variant="body2"
              sx={{
                mb: 1,
                fontWeight: 500,
                color: colors.text.primary,
                fontSize: { xs: '0.875rem', sm: '0.9rem' }
              }}
            >
              Price (per month)
            </Typography>
            <TextField
              fullWidth
              type="number"
              value={formData.price_per_month}
              onChange={(e) => handleInputChange('price_per_month', e.target.value)}
              onBlur={() => handleBlur('price_per_month')}
              error={Boolean(errors.price_per_month)}
              helperText={errors.price_per_month}
              disabled={saving}
              placeholder="Enter price per month"
              inputProps={{ step: '0.01', min: 0 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PriceIcon sx={{ fontSize: 20, color: colors.primary.main }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  '&:hover fieldset': {
                    borderColor: colors.primary.main,
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: colors.primary.main,
                  },
                },
              }}
            />
          </Box>

          <Box>
            <Typography
              variant="body2"
              sx={{
                mb: 1,
                fontWeight: 500,
                color: colors.text.primary,
                fontSize: { xs: '0.875rem', sm: '0.9rem' }
              }}
            >
              Number of Projects
            </Typography>
            <TextField
              fullWidth
              type="number"
              value={formData.number_of_projects}
              onChange={(e) => handleInputChange('number_of_projects', e.target.value)}
              onBlur={() => handleBlur('number_of_projects')}
              error={Boolean(errors.number_of_projects)}
              helperText={errors.number_of_projects}
              disabled={saving}
              placeholder="Enter number of projects"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <ProjectsIcon sx={{ fontSize: 20, color: colors.primary.main }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  '&:hover fieldset': {
                    borderColor: colors.primary.main,
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: colors.primary.main,
                  },
                },
              }}
            />
          </Box>

          <Box>
            <Typography
              variant="body2"
              sx={{
                mb: 1,
                fontWeight: 500,
                color: colors.text.primary,
                fontSize: { xs: '0.875rem', sm: '0.9rem' }
              }}
            >
              Members Per Project
            </Typography>
            <TextField
              fullWidth
              type="number"
              value={formData.number_of_members_per_project}
              onChange={(e) => handleInputChange('number_of_members_per_project', e.target.value)}
              onBlur={() => handleBlur('number_of_members_per_project')}
              error={Boolean(errors.number_of_members_per_project)}
              helperText={errors.number_of_members_per_project}
              disabled={saving}
              placeholder="Enter members per project"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <MembersIcon sx={{ fontSize: 20, color: colors.primary.main }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  '&:hover fieldset': {
                    borderColor: colors.primary.main,
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: colors.primary.main,
                  },
                },
              }}
            />
          </Box>

          <Box>
            <Typography
              variant="body2"
              sx={{
                mb: 1,
                fontWeight: 500,
                color: colors.text.primary,
                fontSize: { xs: '0.875rem', sm: '0.9rem' }
              }}
            >
              Storage (MB)
            </Typography>
            <TextField
              fullWidth
              type="number"
              value={formData.storage}
              onChange={(e) => handleInputChange('storage', e.target.value)}
              onBlur={() => handleBlur('storage')}
              error={Boolean(errors.storage)}
              helperText={errors.storage}
              disabled={saving}
              placeholder="Enter storage in MB"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <StorageIcon sx={{ fontSize: 20, color: colors.primary.main }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  '&:hover fieldset': {
                    borderColor: colors.primary.main,
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: colors.primary.main,
                  },
                },
              }}
            />
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: { xs: 2, sm: 3 }, gap: 1 }}>
        <Button
          onClick={handleClose}
          disabled={saving}
          variant="outlined"
          sx={{
            minWidth: { xs: 80, sm: 100 },
            textTransform: 'none',
            fontWeight: 600,
            borderColor: colors.alpha.black08,
            color: colors.text.secondary,
            '&:hover': {
              borderColor: colors.text.secondary,
            }
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          disabled={saving}
          variant="contained"
          sx={{
            minWidth: { xs: 80, sm: 100 },
            textTransform: 'none',
            fontWeight: 600,
            bgcolor: colors.primary.main,
            '&:hover': {
              bgcolor: colors.primary.dark,
            }
          }}
        >
          {saving ? 'Saving...' : 'Save Changes'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditSubscriptionDialog;
